import React, { useState } from "react";
import { Box, SimpleGrid, Image, Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter, Button, Text, Heading, useDisclosure } from "@chakra-ui/react";
import { FiEdit, FiPlus } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import {AppDispatch} from "~/store";
import {useDispatch} from "react-redux";
import {addImageToLibrary, getReplaceUrl} from "~/store/imageSlice.ts";

interface ImageItem {
    id: string;
    public_id: string;
    title: string;
    description: string;
    dimensions: string;
    src: string;
    size: string;
}

interface ImageListProps {
    images: ImageItem[];
    type: "product" | "theme";
}

const ImageList: React.FC<ImageListProps> = ({ images }) => {
    const { isOpen, onOpen, onClose } = useDisclosure();
    const [selectedImage, setSelectedImage] = useState<ImageItem | null>(null);
    const navigate = useNavigate();
    const dispatch = useDispatch<AppDispatch>();
    const handleImageClick = (image: ImageItem) => {
        setSelectedImage(image);
        onOpen();
    };

    const handleEditClick = (image: ImageItem) => {
        if ( image.id ) {
            // Shopify product image, first add it to library
            dispatch(
                addImageToLibrary({
                    shopify_product_id: image.id,
                    shopify_src: image.src,
                })
            ).unwrap().then((r) => {
                navigate(`/c-panel/cartario/image/edit/${r?.public_id}`);
            });
        } else {
            navigate(`/c-panel/cartario/image/edit/${image?.public_id}`);
        }
    };

    const handleReplaceClick = (image: ImageItem) => {
        const data = {
            dimensions: image.dimensions,
            product_id: image?.public_id || image.id,
            is_shopify_product: !!image?.public_id
        }
        dispatch(
            getReplaceUrl(
                data
            )
        ).unwrap().then((r) => {
            navigate(r);
        })
    };

    return (
        <Box p={10}>
            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
                {images.map((image, index) => (
                    <Box
                        key={index}
                        bg="gray.700"
                        borderRadius="lg"
                        overflow="hidden"
                        cursor="pointer"
                        transition="transform 0.2s"
                        _hover={{ transform: "scale(1.05)" }}
                        onClick={() => handleImageClick(image)}
                    >
                        <Image src={image.src} alt={image.title} width="100%" />
                        <Box p={4}>
                            <Heading size="sm" color="white">
                                {image.title}
                            </Heading>
                            <Text color="gray.400">{image.description}</Text>
                        </Box>
                    </Box>
                ))}
            </SimpleGrid>

            {/* Modal for Image Details */}
            {selectedImage && (
                <Modal isOpen={isOpen} onClose={onClose} size="lg" isCentered>
                    <ModalOverlay />
                    <ModalContent bg="gray.800" color="white">
                        <ModalHeader>{selectedImage.title}</ModalHeader>
                        <ModalBody>
                            <Image src={selectedImage.src} alt={selectedImage.title} borderRadius="md" mb={4} />
                            <Text>Description: {selectedImage.description}</Text>
                            <Text mt={2}>
                                Dimensions: {selectedImage.dimensions}
                            </Text>
                        </ModalBody>
                        <ModalFooter>
                            <Box width="100%">
                                <Button
                                    leftIcon={<FiEdit />}
                                    colorScheme="teal"
                                    width="100%"
                                    mb={3}
                                    onClick={() => handleEditClick(selectedImage)}
                                >
                                    Edit This Image
                                </Button>
                                <Button
                                    leftIcon={<FiPlus />}
                                    colorScheme="blue"
                                    width="100%"
                                    onClick={() => handleReplaceClick(selectedImage)}
                                >
                                    Create New Image with Same Aspect Ratio
                                </Button>
                            </Box>
                        </ModalFooter>
                    </ModalContent>
                </Modal>
            )}
        </Box>
    );
};

export default ImageList;
